/**
 * Created by Juno Peng 2/18/2024
 * Description:
 * AdminCentralConfig Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { CentralConfig } from './admin-central-config.model';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { NotificationStyles, NotificationOptions } from '../../../../widgets/notification/notification.model';
import { CentralConfigService } from '../../../../services/central-config.service';

@Component({
    selector: 'tam-admin-central-config',
    templateUrl: './admin-central-config.component.html',
    styleUrls: ['./admin-central-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class AdminCentralConfigComponent implements OnInit, OnDestroy {
    centralCnfigProperties: CentralConfig;

    centralConfigForm = new UntypedFormGroup({
        host: new UntypedFormControl(''),
        port: new UntypedFormControl(''),
        SSL: new UntypedFormControl(false),
        refdatasourceHost: new UntypedFormControl(''),
        refdatasourcePort: new UntypedFormControl(''),
        refdatasourceUsername: new UntypedFormControl(''),
        refdatasourcePassword: new UntypedFormControl(''),
        currentReferenceDataVersion: new UntypedFormControl(''),
        refdataDataSet: new UntypedFormControl({}),
        connectionRetries: new UntypedFormControl(''),
        refdataSSL: new UntypedFormControl(false),
        initialLoad: new UntypedFormControl(false),
    });

    isLoadingSubmit = false;
    isChanged = false;

    changeDataSet = [
        { stringValue: 'NOT AMERICAN', type: 0 },
        { stringValue: 'BASIC', type: 1 },
        { stringValue: 'EXTENDED', type: 2 }
    ];
    changeConnectionRetries = [40, 20, 10, 3, 1, 0];


    get host() {
        return this.centralConfigForm.get('host');
    }

    get port() {
        return this.centralConfigForm.get('port');
    }

    get SSL() {
        return this.centralConfigForm.get('SSL');
    }

    get refdatasourceHost() {
        return this.centralConfigForm.get('refdatasourceHost');
    }

    get refdatasourcePort() {
        return this.centralConfigForm.get('refdatasourcePort');
    }

    get refdatasourceUsername() {
        return this.centralConfigForm.get('refdatasourceUsername');
    }

    get refdatasourcePassword() {
        return this.centralConfigForm.get('refdatasourcePassword');
    }

    get currentReferenceDataVersion() {
        return this.centralConfigForm.get('currentReferenceDataVersion');
    }

    get refdataDataSet() {
        return this.centralConfigForm.get('refdataDataSet');
    }

    get connectionRetries() {
        return this.centralConfigForm.get('connectionRetries');
    }

    get refdataSSL() {
        return this.centralConfigForm.get('refdataSSL');
    }

    get initialLoad() {
        return this.centralConfigForm.get('initialLoad');
    }
    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    //#endregion

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _centralConfigService: CentralConfigService,
        private _toastService: ToastService) { }

    //#region public methods

    ngOnInit() {
        this._getCentralConfig();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    clickRegisterServer() {
        this._setgetRegisterServer();
    }

    /*
     *  verify contorls' status if the control's value is changed
    */
    clickCentralConfigChanged() {
        this.isChanged = true;
        this._doValidation();
    }

    changeRefdataSSLStatus() {
        this.clickCentralConfigChanged();
    }

    /**
     *  Click submit button and send to server.
     */

    clickSubmitCentralConfig() {
        this.isLoadingSubmit = true;
        if (this._doValidation()) {
            this.centralCnfigProperties.host = this.host.value;
            this.centralCnfigProperties.port = this.port.value;
            this.centralCnfigProperties.SSL = this.SSL.value;
            this.centralCnfigProperties.refdatasourceHost = this.refdatasourceHost.value;
            this.centralCnfigProperties.refdatasourcePort = this.refdatasourcePort.value;
            this.centralCnfigProperties.refdatasourceUsername = this.refdatasourceUsername.value;
            this.centralCnfigProperties.refdatasourcePassword = this.refdatasourcePassword.value;
            this.centralCnfigProperties.currentReferenceDataVersion = this.currentReferenceDataVersion.value;
            this.centralCnfigProperties.refdataDataSet = {
                stringValue: this.refdataDataSet.value.stringValue,
                type: this.refdataDataSet.value.type
            };
            this.centralCnfigProperties.connectionRetries = this.connectionRetries.value;
            this.centralCnfigProperties.refdataSSL = this.refdataSSL.value;
            this.centralCnfigProperties.initialLoad = this.initialLoad.value;
            this._setMailSettingProperties();
        } else {
            this.isLoadingSubmit = false;
        }
    }

    //#endregion


    //#region private methods

    /**
     * validation for form control.
     * @returns dataValid
     */
    private _doValidation(): boolean {
        let inValid = true;
        if (!this.host.value) {
            this.host.setErrors({
                required: true
            });
            inValid = false;
        }
        if (this.refdataSSL.value) {
            if (!this.refdatasourceUsername.value) {
                this.refdatasourceUsername.setErrors({
                    required: true
                });
                inValid = false;
            }
            if (!this.refdatasourcePassword.value) {
                this.refdatasourcePassword.setErrors({
                    required: true
                });
                inValid = false;
            }

            if (!this.refdatasourceHost.value) {
                this.refdatasourceHost.setErrors({
                    required: true
                });
                inValid = false;
            }
            if (!this.refdatasourcePort.value) {
                this.refdatasourcePort.setErrors({
                    required: true
                });
                inValid = false;
            }
        }

        return inValid;
    }

    /**
     * Init email setting data from server.
     * @param data
     */
    private _initCentralConfigData(data: CentralConfig) {
        this.host.setValue(this.centralCnfigProperties.host);
        this.port.setValue(this.centralCnfigProperties.port);
        this.SSL.setValue(this.centralCnfigProperties.SSL);
        this.refdatasourceHost.setValue(this.centralCnfigProperties.refdatasourceHost);
        this.refdatasourcePort.setValue(this.centralCnfigProperties.refdatasourcePort);
        this.refdatasourceUsername.setValue(this.centralCnfigProperties.refdatasourceUsername);
        this.refdatasourcePassword.setValue(this.centralCnfigProperties.refdatasourcePassword);
        this.currentReferenceDataVersion.setValue(this.centralCnfigProperties.currentReferenceDataVersion);
        this.refdataDataSet.setValue(this.centralCnfigProperties.refdataDataSet);
        this.connectionRetries.setValue(this.centralCnfigProperties.connectionRetries);
        this.refdataSSL.setValue(this.centralCnfigProperties.refdataSSL);
        this.initialLoad.setValue(this.centralCnfigProperties.initialLoad);
    }

    /**
     * get central config from sever.
     */
    private _getCentralConfig() {
        this._centralConfigService.getCentralConfigProperties().pipe(take(1)).subscribe(data => {
            this.centralCnfigProperties = CentralConfig.parse(data);
            this._initCentralConfigData(this.centralCnfigProperties);
            this._changeDetectorRef.detectChanges();
        }, error => {
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    /**
     * Submit Central Config properties.
     */
    private _setMailSettingProperties() {
        this._centralConfigService.setCentralConfigProperties(this.centralCnfigProperties).pipe(take(1)).subscribe(data => {
            this.isLoadingSubmit = false;
            this.isChanged = false;
            this._changeDetectorRef.detectChanges();
            if (data) {
                this._toast(NotificationStyles.Success, StringLiteralsPipe.translate('email.all_settings_are_submitted_successfully'));
            }
        }, error => {
            this.isLoadingSubmit = false;
            this._changeDetectorRef.detectChanges();
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    /**
     * set central register.
     */
    private _setgetRegisterServer() {
        this._centralConfigService.getRegisterServer().pipe(take(1)).subscribe(data => {
            if (data) {
                if (data.message?.toLowerCase().includes('error')) {
                    this._toast(NotificationStyles.Error, data.message);
                } else {
                    this._toast(NotificationStyles.Success, data.message);
                }
            }
            this._changeDetectorRef.detectChanges();
        }, error => {
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    /**
     * unify toast notification.
     * @param type
     * @param msg
     */
    private _toast(type: NotificationStyles, msg: string) {
        const note = new NotificationOptions();
        note.style = type;
        note.message = msg;
        this._toastService.notify(note);
        this._changeDetectorRef.detectChanges();
    }

    //#endregion
}
